import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Clock, RotateCcw, X, Sparkles, Edit3, Timer, Loader } from 'lucide-react';
import { reportApi, ReportVersionSummary } from '../services/reportApi';
import { formatDistanceToNow } from 'date-fns';

interface VersionHistoryPanelProps {
  reportId: string;
  onClose: () => void;
  onRevert: (content: string) => void;
}

const CHANGE_TYPE_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  manual_edit: { label: 'Manual edit', icon: <Edit3 className="h-3 w-3" />, color: 'text-gray-600' },
  ai_revision: { label: 'AI revision', icon: <Sparkles className="h-3 w-3" />, color: 'text-purple-600' },
  ai_selection_revision: { label: 'AI selection', icon: <Sparkles className="h-3 w-3" />, color: 'text-purple-500' },
  ai_generation: { label: 'AI generation', icon: <Sparkles className="h-3 w-3" />, color: 'text-cyan-600' },
  periodic_snapshot: { label: 'Auto-snapshot', icon: <Timer className="h-3 w-3" />, color: 'text-gray-400' },
  revert: { label: 'Revert', icon: <RotateCcw className="h-3 w-3" />, color: 'text-orange-600' },
};

export const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  reportId,
  onClose,
  onRevert,
}) => {
  const queryClient = useQueryClient();
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  const { data: versions = [], isLoading } = useQuery(
    ['reportVersions', reportId],
    () => reportApi.getVersions(reportId),
    { staleTime: 10_000 }
  );

  const revertMutation = useMutation(
    (versionId: string) => reportApi.revertToVersion(reportId, versionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['report', reportId]);
        queryClient.invalidateQueries(['reportVersions', reportId]);
        reportApi.getReport(reportId).then(report => {
          const contentStr = typeof report.content === 'string'
            ? report.content
            : JSON.stringify(report.content);
          onRevert(contentStr);
        });
      },
    }
  );

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-2xl border-l border-gray-200 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-gray-600" />
          <span className="text-sm font-semibold text-gray-800">Version History</span>
        </div>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Version list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-400">
            No version history yet. Versions are created automatically before AI revisions and periodically during editing.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {versions.map((v) => {
              const meta = CHANGE_TYPE_META[v.changeType] || CHANGE_TYPE_META.manual_edit;
              const isSelected = selectedVersionId === v.id;

              return (
                <div
                  key={v.id}
                  className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                    isSelected ? 'bg-cyan-50 border-l-2 border-cyan-500' : ''
                  }`}
                  onClick={() => setSelectedVersionId(isSelected ? null : v.id)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className={`flex items-center gap-1.5 text-xs font-medium ${meta.color}`}>
                      {meta.icon}
                      {meta.label}
                    </div>
                    <span className="text-[10px] text-gray-400">v{v.versionNumber}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(v.createdAt), { addSuffix: true })}
                  </div>
                  {v.changeDescription && (
                    <p className="text-[11px] text-gray-400 mt-1 truncate italic">
                      {v.changeDescription}
                    </p>
                  )}

                  {isSelected && (
                    <div className="mt-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          revertMutation.mutate(v.id);
                        }}
                        disabled={revertMutation.isLoading}
                        className="w-full flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded hover:bg-orange-100 transition-colors disabled:opacity-50"
                      >
                        <RotateCcw className="h-3 w-3" />
                        {revertMutation.isLoading ? 'Reverting...' : 'Revert to this version'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
